'use client';

import React, { useState, useEffect } from 'react';
import { Check, X, Zap, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

interface WinewoodPlusModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextHint?: string;
}

const PLUS_FEATURES = [
  'Exklusive WINEWOOD+ Rabatte',
  'Wegbeschreibungen zu EpicLocations',
  'Mitglieder only Erlebnisse',
  'Zugang zu WINEWOOD+ Angeboten',
];

type PlanKey = 'yearly' | 'monthly';

interface PlanPrices {
  monthly: number;
  yearly: number;
}

function formatEur(amount: number): string {
  return amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function calcMonthlyEquivalent(yearly: number): string {
  return (yearly / 12).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function calcSavingsPercent(monthly: number, yearly: number): number {
  const annualMonthly = monthly * 12;
  if (annualMonthly <= 0) return 0;
  return Math.round(((annualMonthly - yearly) / annualMonthly) * 100);
}

export default function WinewoodPlusModal({ isOpen, onClose, contextHint }: WinewoodPlusModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('yearly');
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState<PlanPrices>({ monthly: 3.99, yearly: 39.90 });
  const { user } = useAuth();

  useEffect(() => {
    if (!isOpen) return;
    const supabase = createClient();
    supabase
      .from('subscription_plans')
      .select('price_monthly, price_yearly')
      .eq('slug', 'plus')
      .single()
      .then(({ data }) => {
        if (data) {
          setPrices({
            monthly: parseFloat(data.price_monthly) || 3.99,
            yearly: parseFloat(data.price_yearly) || 39.90,
          });
        }
      });
  }, [isOpen]);

  if (!isOpen) return null;

  const savingsPercent = calcSavingsPercent(prices.monthly, prices.yearly);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/stripe/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: selectedPlan,
          userId: user?.id || '',
          email: user?.email || '',
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Checkout fehlgeschlagen');
      }
    } catch (err: any) {
      import('sonner').then(({ toast }) => {
        toast.error('Fehler beim Öffnen der Zahlung', {
          description: err.message || 'Bitte versuche es erneut.',
          icon: '⚠️',
        });
      });
      setLoading(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4"
        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal card — centered, slim */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center px-4 pointer-events-none"
      >
        <div
          className="w-full pointer-events-auto relative"
          style={{
            maxWidth: '380px',
            background: '#2a2d35',
            borderRadius: '20px',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="WINEWOOD+ abonnieren"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'rgba(255,255,255,0.1)' }}
            aria-label="Schließen"
          >
            <X size={15} color="rgba(255,255,255,0.7)" />
          </button>

          {/* Header */}
          <div className="px-5 pt-5 pb-4 flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: '#4a5a2a' }}
            >
              <Zap size={22} color="#a3c44a" fill="#a3c44a" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white leading-tight">WINEWOOD+</h2>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Mehr Abenteuer. Mehr Rabatte.</p>
            </div>
          </div>

          {/* Context hint banner */}
          {contextHint && (
            <div
              className="mx-5 mb-4 px-4 py-3 rounded-xl"
              style={{ background: 'rgba(163,196,74,0.12)', border: '1px solid rgba(163,196,74,0.25)' }}
            >
              <p className="text-xs font-medium leading-relaxed" style={{ color: '#a3c44a' }}>
                {contextHint}
              </p>
            </div>
          )}

          {/* Features */}
          <div className="px-5 pb-4 space-y-2.5">
            {PLUS_FEATURES.map((feature, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#a3c44a' }} />
                <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.85)' }}>{feature}</span>
              </div>
            ))}
          </div>

          {/* Plan cards */}
          <div className="px-5 pb-4 space-y-2.5">
            {/* Yearly */}
            <button
              onClick={() => setSelectedPlan('yearly')}
              className="w-full text-left relative transition-all duration-150 active:scale-[0.99]"
              style={{
                background: selectedPlan === 'yearly' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: `1.5px solid ${selectedPlan === 'yearly' ? '#a3c44a' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '14px',
                padding: '14px 16px',
              }}
            >
              {/* Savings badge */}
              {savingsPercent > 0 && (
                <span
                  className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] font-bold rounded-full"
                  style={{ background: '#a3c44a', color: '#1a1c22' }}
                >
                  −{savingsPercent}% GÜNSTIGER
                </span>
              )}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-white">Jährlich</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    entspricht {calcMonthlyEquivalent(prices.yearly)}/Monat
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-white">{formatEur(prices.yearly)}</span>
                  <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/Jahr</span>
                </div>
              </div>
            </button>

            {/* Monthly */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className="w-full text-left transition-all duration-150 active:scale-[0.99]"
              style={{
                background: selectedPlan === 'monthly' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                border: `1.5px solid ${selectedPlan === 'monthly' ? '#a3c44a' : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '14px',
                padding: '14px 16px',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-bold text-white">Monatlich</p>
                  <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Flexibel jeden Monat kündbar</p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-white">{formatEur(prices.monthly)}</span>
                  <span className="text-xs ml-1" style={{ color: 'rgba(255,255,255,0.45)' }}>/Monat</span>
                </div>
              </div>
            </button>
          </div>

          {/* CTA */}
          <div className="px-5 pb-5">
            <button
              onClick={handleSubscribe}
              disabled={loading}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-sm font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-70"
              style={{
                background: '#a3c44a',
                color: '#1a1c22',
              }}
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Zap size={16} fill="#1a1c22" />
              )}
              {loading ? 'Weiterleitung...' : 'Jetzt WINEWOOD+ holen'}
            </button>
            <p className="text-center text-[10px] mt-2.5" style={{ color: 'rgba(255,255,255,0.3)' }}>
              Sichere Zahlung über Stripe · Jederzeit in der App kündbar
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
